import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbPage,
} from "@/components/admin/breadcrumb";
import { cn } from "@/lib/utils";
import type { EditBaseProps } from "ra-core";
import {
  EditBase,
  Translate,
  useCreatePath,
  useEditContext,
  useGetRecordRepresentation,
  useGetResourceLabel,
  useHasDashboard,
  useResourceContext,
} from "ra-core";
import type { ReactNode } from "react";
import { Link } from "react-router";

export interface EditProps extends EditViewProps, EditBaseProps {}

/**
 * A complete edit page with breadcrumb, title, and default actions.
 */
export const Edit = ({
  actions,
  children,
  className,
  disableBreadcrumb,
  title,
  ...rest
}: EditProps) => (
  <EditBase {...rest}>
    <EditView
      actions={actions}
      className={className}
      disableBreadcrumb={disableBreadcrumb}
      title={title}
    >
      {children}
    </EditView>
  </EditBase>
);

export interface EditViewProps {
  disableBreadcrumb?: boolean;
  title?: ReactNode | string | false;
  actions?: ReactNode;
  children?: ReactNode;
  className?: string;
}

/**
 * The view component for Edit pages with layout and UI.
 */
export const EditView = ({
  disableBreadcrumb,
  title,
  actions,
  className,
  children,
}: EditViewProps) => {
  const context = useEditContext();

  const resource = useResourceContext();
  if (!resource) {
    throw new Error(
      "The EditView component must be used within a ResourceContextProvider",
    );
  }
  const getResourceLabel = useGetResourceLabel();
  const listLabel = getResourceLabel(resource, 2);
  const createPath = useCreatePath();
  const listLink = createPath({
    resource,
    type: "list",
  });

  const getRecordRepresentation = useGetRecordRepresentation(resource);
  const recordRepresentation = getRecordRepresentation(context.record);

  const hasDashboard = useHasDashboard();

  if (context.isLoading || !context.record) {
    return null;
  }

  return (
    <div
      data-slot="edit-view"
      /*please use some better colour combo*/
      className="relative -top-4 lg:-top-4 -mb-4 w-auto min-h-screen bg-[#f0f4f9] dark:bg-slate-950 -mx-4 lg:-mx-8 px-4 lg:px-8 pb-16 flex flex-col"
    >
      {/* ── FIXED FULL-WIDTH HEADER (STAYS FIXED ON TOP) ── */}
      <div
        className={cn(
          "sticky top-0 z-50 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 py-4 px-4 lg:px-8 -mx-4 lg:-mx-8 mb-6 flex justify-center w-auto shadow-sm",
          className,
        )}
      >
        <div className="max-w-5xl w-full flex justify-between items-center flex-wrap gap-4">
          <div className="text-xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
            {title !== undefined ? title : context.defaultTitle}
          </div>
          <div
            id="ra-form-actions-portal"
            className="flex items-center gap-2 relative z-50"
          >
            {actions !== undefined ? actions : null}
          </div>
        </div>
      </div>

      {/* ── CONTENT AREA ── */}
      <div className="max-w-5xl w-full mx-auto flex-1 flex flex-col px-2">
        {!disableBreadcrumb && (
          <Breadcrumb className="mb-6">
            {hasDashboard && (
              <BreadcrumbItem>
                <Link to="/">
                  <Translate i18nKey="ra.page.dashboard">Home</Translate>
                </Link>
              </BreadcrumbItem>
            )}
            <BreadcrumbItem>
              <Link to={listLink}>{listLabel}</Link>
            </BreadcrumbItem>
            <BreadcrumbPage>{recordRepresentation}</BreadcrumbPage>
          </Breadcrumb>
        )}
        <div className="flex-1">{children}</div>
      </div>
    </div>
  );
};
